import React, { Component } from 'react';
import SideMenu from '../admin/SideMenu';
import TopMenu from '../admin/TopBar';
import { appDatabasePrimaryFunctions } from '../../base';
import '../../styles/css/AdminMain.css';
import Swal from 'sweetalert2';
import {
    offerWindowState, dateTimeInputToEpoch, epochToDateTimeInput, describeWindow,
    describeCodeExpiry, OFFER_SCHEDULED, OFFER_ENDED
} from '../utils/offerSchedule';
import BasicDropzone from '../utils/Dropzone';
import { ErrorMessage, Toast, readApiResponse } from '../utils/HelpfulFunction';
import UploadImage from '../utils/UploadImage';
import { Modal } from 'reactstrap';
import { getScope, subscribeToScope, loadExperiences } from '../utils/experienceScope';
import { identityIsVerifiable } from '../utils/featureAvailability';
import { loadServerCapabilities } from '../utils/serverCapabilities';
import {
    validCodePrefix, hasFixedLength, codeLengthProblem, accountDigitsThatFit, buildDerivedCode,
    MAX_CODE_LENGTH_DERIVED
} from '../utils/derivedOfferCode';
import { teamIdOptions } from '../utils/experienceScope';
import {
    offerMatchesExperience,
    experienceFieldsFor,
    currentSelectionFor,
    isSharedAcrossExperiences
} from '../utils/offerExperiences';

// Offers v2 — sponsor/team offers fans see in the app. Link offers click
// through to a URL; coded offers hand each fan a unique code, either from a
// pool uploaded here or built from the fan's verified Ticketmaster account
// (codePoolSize/codesAssigned are maintained server-side).

// API URL for Firebase Functions
let apiUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api/`;
let usingLocalApi = false;
if (process.env.NODE_ENV === "development") {
    apiUrl = `http://localhost:5001/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/us-central1/api/`;
    usingLocalApi = true;
}

// A thrown fetch means the request never got an answer. Running locally that is
// almost always the functions emulator not being up — blaming the operator's
// connection sends them to check their wifi while the real fix is one command.
export function uploadFailureText(isLocal) {
    return isLocal
        ? 'Could not reach the functions emulator at localhost:5001. Start it with `npm run serve` in the functions folder, then try again.'
        : 'Something went wrong uploading the codes, check your connection and try again.';
}

class SetUpOffers extends Component {
    constructor(props) {
        super(props);
        this.UploadImage = new UploadImage();
        this.state = {
            offers: {},
            experiences: {},
            loading: true,
            token: null,
            editingId: null,
            step: 'details',
            title: '',
            description: '',
            imageUrl: '',
            imageUrlPreview: {},
            audience: 'all',
            team: '',
            type: 'link',
            codeSource: 'pool',
            serverCapabilities: null,
            codePrefix: '',
            codeLength: '',
            showOtherExperiences: false,
            startsAt: '',
            endsAt: '',
            expiresAtHours: '',
            expiresAtDateTime: '',
            buttonText: '',
            clickUrl: '',
            order: '',
            active: true,
            codesText: '',
            // The experience is the top-bar scope, not a field on this form —
            // an offer belongs to the experience you are working in.
            scope: getScope(),
            modal: false,
            // real experience ids this offer appears in
            selectedExperienceIds: []
        };
        this.handleChange = this.handleChange.bind(this);
        this.toggleModal = this.toggleModal.bind(this);
        this.saveOffer = this.saveOffer.bind(this);
        this.clearForm = this.clearForm.bind(this);
        this.onDrop = this.onDrop.bind(this);
        this.uploadCodes = this.uploadCodes.bind(this);
        this.finishLater = this.finishLater.bind(this);
        this.finishCodes = this.finishCodes.bind(this);
    }

    async componentDidMount() {
        this.unsubscribeScope = subscribeToScope(scope =>
            this.setState({ scope, editingId: null, modal: false }));
        loadExperiences().then(experienceList => this.setState({ experienceList }));
        // Whether a code can be built from a fan's Ticketmaster account depends
        // on a key in the functions environment, not on anything this browser
        // can see. Null until it answers, and null shows the option — the same
        // bargain Feature Access makes.
        loadServerCapabilities(this.props.userData)
            .then(serverCapabilities => this.setState({ serverCapabilities }));
        await this.loadData();

        // Get auth token for API calls (code uploads)
        if (this.props.userData) {
            const token = await this.props.userData.getIdToken();
            this.setState({ token });
        }
    }

    async loadData() {
        try {
            const [offersSnap, experiencesSnap] = await Promise.all([
                appDatabasePrimaryFunctions.ref('offers').once('value'),
                appDatabasePrimaryFunctions.ref('experiences').once('value')
            ]);
            this.setState({
                offers: offersSnap.val() || {},
                experiences: experiencesSnap.val() || {},
                loading: false
            });
        } catch (e) {
            console.log('Error loading offers:', e);
            this.setState({ loading: false });
        }
    }

    componentWillUnmount() {
        if (this.unsubscribeScope) this.unsubscribeScope();
    }

    toggleModal() {
        // Closing always resets, so a half-filled form never leaks into the
        // next thing you open.
        if (this.state.modal) this.clearForm();
        else this.setState({ modal: true });
    }

    handleChange(evt) {
        const target = evt.target;
        this.setState({ [target.name]: target.type === 'checkbox' ? target.checked : target.value });
    }

    clearForm() {
        this.setState({
            editingId: null,
            step: 'details',
            title: '',
            description: '',
            imageUrl: '',
            imageUrlPreview: {},
            audience: 'all',
            team: '',
            type: 'link',
            codeSource: 'pool',
            codePrefix: '',
            codeLength: '',
            showOtherExperiences: false,
            startsAt: '',
            endsAt: '',
            expiresAtHours: '',
            expiresAtDateTime: '',
            buttonText: '',
            clickUrl: '',
            order: '',
            active: true,
            codesText: '',
            modal: false,
            selectedExperienceIds: this.defaultSelection()
        });
    }

    // Offers do not need experiences. A club can have none at all: its offers
    // live at the root `offers` node with experienceId '' — the default
    // experience by convention — and both matchers already read that as "shows
    // for everyone" when there is no experience to be in. So everywhere the
    // copy names the experience an offer runs in, there may be nothing to name,
    // and asking someone to pick one would make offers impossible.
    hasExperiences() {
        return this.experienceOptions().length > 0;
    }

    // The experience list, as real ids — the default experience has one too.
    experienceOptions() {
        return Object.entries(this.state.experiences || {})
            .map(([id, exp]) => ({ id, ...exp }))
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    }

    // A new offer belongs to the experience you are looking at.
    defaultSelection() {
        const options = this.experienceOptions();
        const scope = this.state.scope;
        if (scope) return [scope];
        const dflt = options.find(exp => exp.isDefault);
        return dflt ? [dflt.id] : [];
    }

    toggleExperience(id) {
        this.setState(prev => {
            const next = prev.selectedExperienceIds.indexOf(id) !== -1
                ? prev.selectedExperienceIds.filter(x => x !== id)
                : prev.selectedExperienceIds.concat(id);
            return { selectedExperienceIds: next };
        });
    }

    startEdit(id) {
        const offer = this.state.offers[id] || {};
        this.setState({
            editingId: id,
            title: offer.title || '',
            description: offer.description || '',
            imageUrl: offer.imageUrl || '',
            imageUrlPreview: {},
            audience: offer.audience || 'all',
            team: offer.team || '',
            type: offer.type || 'link',
            // Offers made before derived codes existed carry no codeSource,
            // and they are all pools.
            codeSource: offer.codeSource || 'pool',
            codePrefix: offer.codePrefix || '',
            codeLength: offer.codeLength > 0 ? String(offer.codeLength) : '',
            showOtherExperiences: currentSelectionFor(offer, this.experienceOptions()).length > 1,
            startsAt: epochToDateTimeInput(offer.startsAt),
            endsAt: epochToDateTimeInput(offer.endsAt),
            expiresAtHours: offer.expiresAtHours > 0 ? String(offer.expiresAtHours) : '',
            expiresAtDateTime: epochToDateTimeInput(offer.expiresAtDateTime),
            buttonText: offer.buttonText || '',
            clickUrl: offer.clickUrl || '',
            order: offer.order || '',
            active: offer.active !== false,
            codesText: '',
            modal: true,
            selectedExperienceIds: currentSelectionFor(offer, this.experienceOptions())
        });
    }

    onDrop(files, rejected, fieldName) {
        if (rejected.length > 0) {
            ErrorMessage.fire({
                title: 'Image cannot be uploaded',
                text: 'Make sure the image is less than 2mbs and it is an accepted file type'
            });
            return;
        }
        this.setState({ loading: true });

        this.UploadImage.upload_file(files[0]).then(res => {
            this.setState({ loading: false });
            if (res.error) {
                ErrorMessage.fire({
                    title: 'Image cannot be uploaded',
                    text: res.error,
                });
            } else if (res.imageUrl) {
                this.setState({
                    [fieldName]: res.imageUrl,
                    imageUrlPreview: {
                        ...this.state.imageUrlPreview,
                        [fieldName]: {
                            file: files[0],
                            preview: URL.createObjectURL(files[0])
                        }
                    }
                });
            } else {
                ErrorMessage.fire({
                    title: 'Image cannot be uploaded',
                    text: 'Something went wrong, please re-upload your image and try again!'
                });
            }
        });
    }

    renderImageUpload(label, fieldName) {
        const preview = this.state.imageUrlPreview[fieldName] && this.state.imageUrlPreview[fieldName].preview;
        const savedVersion = this.state[fieldName];
        const hasImage = !!(preview || savedVersion);

        // Was align="center" while every other field on the screen is
        // left-aligned, so the upload sat off-axis from the whole form.
        return (
            <div className="form-group">
                <label htmlFor={fieldName}>{label}</label>
                <p className="admin-hint">1500 × 600 px, JPG or PNG up to 2 MB. In the offer list the image is the whole card, with no title over it, so it should say what the offer is. Keep logos and text in the centre 1200 × 400: older app versions trim the edges.</p>
                <div className="admin-drop-row">
                    {hasImage &&
                        <img
                            src={preview || savedVersion}
                            height="46"
                            width="auto"
                            style={{ borderRadius: 4, maxWidth: 90, objectFit: 'cover' }}
                            alt=""
                        />
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <BasicDropzone
                            onDrop={(acceptedFiles, fileRejections) => this.onDrop(acceptedFiles, fileRejections, fieldName)}
                            preview={preview}
                        />
                    </div>
                    {hasImage &&
                        <button
                            type="button"
                            className="btn btn-sm"
                            style={{ color: 'var(--accent-deep)', border: '1px solid #F0CBD1', background: 'var(--paper)' }}
                            onClick={() => this.setState({
                                [fieldName]: '',
                                imageUrlPreview: {
                                    ...this.state.imageUrlPreview,
                                    [fieldName]: {}
                                }
                            })}
                        >
                            Remove
                        </button>
                    }
                </div>
            </div>
        );
    }

    // How long this offer's codes last, for the list. Null when they never
    // expire, which is most offers and every one that predates the feature.
    codeExpiryNote(offer) {
        if (offer.type !== 'coded') return null;
        const life = describeCodeExpiry(offer);
        if (!life) return null;
        const dead = Number(offer.expiresAtDateTime) > 0 && Number(offer.expiresAtDateTime) <= Date.now();
        return (
            <span
                className={`admin-tag${dead ? ' is-strong' : ''}`}
                style={{ marginLeft: 5 }}
                title={dead ? 'Codes for this offer have expired — no fan can use one' : `Codes work ${life}`}
            >
                {dead ? 'Codes expired' : `Codes ${life.startsWith('until') ? life : `last ${life}`}`}
            </span>
        );
    }

    // A code built from the fan has no pool and cannot run out, so none of the
    // pool machinery applies to it. Without this it reads as an offer in
    // trouble: 'out', styled red, captioned "No codes".
    isDerived(offer) {
        return offer.type === 'coded' && offer.codeSource === 'derived';
    }

    // How many fans already hold a built code for this saved offer. Each one is
    // saved to the fan when first claimed, and the fan app reads it back from
    // there without asking the server again — so changing how the offer builds
    // codes afterwards would only change what LATER fans get, leaving two
    // card-number formats in circulation for one offer. Worse, a new prefix can
    // hand a later fan an earlier fan's card: 26CVF + account 15 and 26CVF1 +
    // account 5 are both 26CVF15. Nonzero locks the type, where the codes come
    // from, the team, the prefix and the length. The team because the account
    // is looked up under it, and the first two because switching an offer to
    // a pool and back clears the prefix and would reopen the rest.
    //
    // `saved` defaults to the list loaded when the page opened, which is fine
    // for drawing the form. saveOffer passes a fresh read, since fans may have
    // claimed while the tab sat open.
    derivedCodesIssued(offerId, saved = offerId ? (this.state.offers || {})[offerId] : null) {
        return saved && this.isDerived(saved) && saved.codesAssigned > 0 ? saved.codesAssigned : 0;
    }

    // How many fans hold a code from an uploaded list on this saved offer. Such
    // an offer cannot become a built one: those fans would keep their list codes
    // while new fans were handed card numbers — two kinds of code on one offer,
    // and a lock that then counts list codes as built ones. Not limited to coded
    // offers, because a list offer switched to a link keeps its count, and
    // switching it on to built codes from there is the same change in two steps.
    // The database rules refuse it too, and they also see assignments.
    listCodesIssued(offerId, saved = offerId ? (this.state.offers || {})[offerId] : null) {
        return saved && saved.codeSource !== 'derived' && saved.codesAssigned > 0 ? saved.codesAssigned : 0;
    }

    // The prefix and length as they would be written — from the form, or from
    // a saved offer — so the two can be compared.
    // A length that could never be used reads as none, the same way startEdit
    // loads it — or a hand-edited -5 would differ from the blank field showing
    // it, and every save of a locked offer would be refused.
    codeFormatOf({ codePrefix, codeLength }) {
        const length = hasFixedLength(codeLength) ? Number(String(codeLength).trim()) : null;
        return {
            codePrefix: (codePrefix || '').trim().toUpperCase() || null,
            codeLength: length > 0 ? length : null
        };
    }

    // Would this save change how an issued offer builds its codes?
    changesIssuedFormat(saved) {
        const form = this.codeFormatOf(this.state);
        const was = this.codeFormatOf(saved);
        return this.state.type !== 'coded'
            || this.state.codeSource !== 'derived'
            || (this.state.team || '') !== (saved.team || '')
            || form.codePrefix !== was.codePrefix
            || form.codeLength !== was.codeLength;
    }

    codeLengthProblemText(problem, prefix = this.state.codePrefix) {
        const prefixLength = (prefix || '').trim().length;
        if (problem === 'too_short') {
            return `The prefix alone is ${prefixLength} characters, so the code has to be longer than that to fit an account number. Leave it blank for no padding.`;
        }
        if (problem === 'too_long') {
            return `A card number can be at most ${MAX_CODE_LENGTH_DERIVED} characters.`;
        }
        return 'Use a whole number of characters, like 14, or leave it blank for no padding.';
    }

    // Prefix and length side by side, and under them the one line that
    // matters: the card number a real account would get. The example beats a
    // description — the operator can check it against the card numbers they
    // gave the point of sale before a single fan sees one.
    renderDerivedCodeFormat() {
        const { codePrefix, codeLength, editingId } = this.state;
        const issued = this.derivedCodesIssued(editingId);
        const prefix = (codePrefix || '').trim().toUpperCase() || '26CVF';
        const EXAMPLE_ACCOUNT = '8462799';
        const lengthProblem = codeLengthProblem(prefix, codeLength);
        const example = buildDerivedCode(prefix, EXAMPLE_ACCOUNT, codeLength);
        const room = accountDigitsThatFit(prefix, codeLength);

        let exampleLine;
        if (lengthProblem) {
            exampleLine = this.codeLengthProblemText(lengthProblem, prefix);
        } else if (example.problem === 'account_too_long') {
            exampleLine = <>
                A fan on account {EXAMPLE_ACCOUNT} would get <b>no code</b>: its {EXAMPLE_ACCOUNT.length} digits
                do not fit in the {room} left after the prefix, and an account is never shortened.
            </>;
        } else {
            exampleLine = <>
                A fan on account {EXAMPLE_ACCOUNT} would see <b>{example.code}</b>
                {room !== null && `, and account numbers up to ${room} digits fit`}
            </>;
        }

        return (
            <div>
                <div className="admin-row">
                    <div className="form-group" style={{ maxWidth: 200 }}>
                        <label htmlFor="codePrefix">Prefix</label>
                        <p className="admin-hint">Goes before the account number.</p>
                        <input
                            type="text"
                            id="codePrefix"
                            name="codePrefix"
                            className="form-control"
                            value={codePrefix}
                            onChange={this.handleChange}
                            placeholder="26CVF"
                            maxLength={16}
                            disabled={issued > 0}
                        />
                    </div>
                    <div className="form-group" style={{ maxWidth: 200 }}>
                        <label htmlFor="codeLength">Code length</label>
                        <p className="admin-hint">Zeros fill the gap. Blank adds none.</p>
                        <input
                            type="number"
                            min="1"
                            max={MAX_CODE_LENGTH_DERIVED}
                            step="1"
                            id="codeLength"
                            name="codeLength"
                            className="form-control"
                            value={codeLength}
                            onChange={this.handleChange}
                            // A locked, empty field showing a grey "14" reads
                            // as a length the offer does not have.
                            placeholder={issued > 0 ? '' : '14'}
                            disabled={issued > 0}
                        />
                    </div>
                </div>
                <p className="admin-hint">{exampleLine}</p>
                {issued > 0 &&
                    <p className="admin-hint">
                        {issued === 1 ? 'A fan already holds' : `${issued} fans already hold`} a code built this way,
                        so the type, the team, where the codes come from, the prefix and the length can no longer change.
                        To change any of them, make a new offer.
                    </p>
                }
            </div>
        );
    }

    // 'out' once the pool is empty, 'low' inside the last 5% (or 10 codes)
    poolState(offer) {
        if (this.isDerived(offer)) return 'ok';
        const pool = offer.codePoolSize || 0;
        const remaining = pool - (offer.codesAssigned || 0);
        if (pool === 0 || remaining <= 0) return 'out';
        if (remaining <= Math.max(10, pool * 0.05)) return 'low';
        return 'ok';
    }

    poolRemaining(offer) {
        const pool = offer.codePoolSize || 0;
        return Math.max(0, pool - (offer.codesAssigned || 0));
    }

    // The roll fills with codes REMAINING, not codes assigned, so it
    // empties as the pool drains. Filling it the other way reads as
    // "plenty left" at the exact moment the pool is about to break.
    renderMiniRoll(offer) {
        if (offer.type !== 'coded') return <span className="admin-mini-n">&mdash;</span>;
        if (this.isDerived(offer)) {
            // What matters for a derived offer is how many fans have one, since
            // there is no remaining count to report.
            const assigned = offer.codesAssigned || 0;
            return <span className="admin-mini-n">Built &middot; {assigned} issued</span>;
        }
        const pool = offer.codePoolSize || 0;
        const remaining = this.poolRemaining(offer);
        const pct = pool > 0 ? (remaining / pool) * 100 : 0;
        let label;
        if (!(pool > 0)) label = 'No codes';
        else if (remaining === 0) label = 'Empty';
        else label = `${remaining} left`;
        return (
            <span className="admin-mini">
                <span className="admin-mini-roll">
                    <span className="admin-mini-fill" style={{ width: `${pct}%` }}/>
                    <span className="admin-mini-perf"/>
                </span>
                <span className="admin-mini-n">{label}</span>
            </span>
        );
    }

    // '' means the default experience (root config) by convention
    experienceName(experienceId) {
        const { experiences } = this.state;
        if (experienceId) {
            const exp = experiences[experienceId];
            return exp ? exp.name : experienceId;
        }
        const defaultExp = Object.values(experiences).find(exp => exp && exp.isDefault);
        return defaultExp ? defaultExp.name : 'Default';
    }

    async saveOffer() {
        const { offers, editingId, title, type, audience, order } = this.state;
        if (!title || title.trim().length === 0) {
            ErrorMessage.fire({ title: 'Missing title', text: 'Give the offer a title — this is what fans see on the offer card.' });
            return;
        }
        if (title.trim().length > 50) {
            ErrorMessage.fire({ title: 'Title too long', text: 'Keep the offer title under 50 characters so it fits on the offer card.' });
            return;
        }
        if (type === 'link' && !(this.state.clickUrl || '').trim()) {
            ErrorMessage.fire({ title: 'Missing link', text: "Link offers need a Click-Through URL — that's what the Redeem button opens." });
            return;
        }
        // Only when there is a choice to make. A tenant with no experiences
        // saves with experienceId '' (see experienceFieldsFor), which is the
        // default experience and what a fan with no experiences matches.
        if (this.hasExperiences() && !this.state.selectedExperienceIds.length) {
            ErrorMessage.fire({
                title: 'Pick at least one experience',
                text: 'An offer has to appear in at least one experience, or no fan can ever see it.'
            });
            return;
        }
        if (audience === 'stm' && !(this.state.team || '').trim()) {
            ErrorMessage.fire({ title: 'Missing team', text: "STM offers need a team — pick whose season ticket members unlock this offer." });
            return;
        }
        // A derived code is built from the account Ticketmaster confirmed for a
        // fan, which only exists on a season-ticket entitlement. Without a team
        // there is nothing to look it up against.
        const derived = type === 'coded' && this.state.codeSource === 'derived';
        // Decided on a fresh read: the list was loaded when the page opened, and
        // a tab left open while fans claim would still show the format as free.
        let saved = editingId ? (offers || {})[editingId] : null;
        if (editingId) {
            try {
                const fresh = await appDatabasePrimaryFunctions.ref(`offers/${editingId}`).once('value');
                if (fresh.exists()) saved = fresh.val();
            } catch (e) {
                console.log('Could not re-read the offer before saving:', e);
            }
        }
        const issued = this.derivedCodesIssued(editingId, saved);
        // Refused, not quietly overwritten: a save that reported success while
        // ignoring what was typed would leave the operator believing it changed.
        if (issued && this.changesIssuedFormat(saved)) {
            ErrorMessage.fire({
                title: 'Codes already issued',
                text: `${issued === 1 ? 'A fan already holds' : `${issued} fans already hold`} a code built from their`
                    + ' Ticketmaster account, so how this offer makes codes can no longer change. To change it, make a new offer.'
            });
            this.setState({
                offers: { ...(offers || {}), [editingId]: saved },
                type: saved.type,
                codeSource: saved.codeSource,
                audience: saved.audience,
                team: saved.team || '',
                codePrefix: saved.codePrefix || '',
                codeLength: saved.codeLength > 0 ? String(saved.codeLength) : ''
            });
            return;
        }
        const listIssued = this.listCodesIssued(editingId, saved);
        if (listIssued && derived) {
            ErrorMessage.fire({
                title: 'Codes already issued',
                text: `${listIssued === 1 ? 'A fan already holds' : `${listIssued} fans already hold`} a code from the`
                    + ' uploaded list, so this offer cannot switch to codes built from their Ticketmaster account — they would'
                    + ' keep those codes while new fans got card numbers. Make a new offer instead.'
            });
            this.setState({
                offers: { ...(offers || {}), [editingId]: saved },
                type: saved.type,
                codeSource: saved.codeSource || 'pool'
            });
            return;
        }
        if (derived) {
            if (audience !== 'stm') {
                ErrorMessage.fire({
                    title: 'Built codes need season ticket members',
                    text: 'A built code comes from the account Ticketmaster confirmed for a fan, so this offer has to be limited to a team\'s season ticket members.'
                });
                return;
            }
            // The server applies the same rules in lib/derivedOfferCode.js;
            // this is the friendly half of them.
            if (!issued && !validCodePrefix((this.state.codePrefix || '').toUpperCase())) {
                ErrorMessage.fire({
                    title: 'Check the prefix',
                    text: 'Use up to 16 capital letters or numbers, like 26CVF. It sits in front of every fan\'s account number to make their card number.'
                });
                return;
            }
            const lengthProblem = issued ? null : codeLengthProblem(this.state.codePrefix, this.state.codeLength);
            if (lengthProblem) {
                ErrorMessage.fire({ title: 'Check the code length', text: this.codeLengthProblemText(lengthProblem) });
                return;
            }
        }
        const startsAt = dateTimeInputToEpoch(this.state.startsAt);
        const endsAt = dateTimeInputToEpoch(this.state.endsAt);
        const rawHours = (this.state.expiresAtHours || '').trim();
        const expiresAtHours = rawHours === '' ? null : Number(rawHours);
        const expiresAtDateTime = dateTimeInputToEpoch(this.state.expiresAtDateTime);
        if (expiresAtHours !== null && !(Number.isFinite(expiresAtHours) && expiresAtHours > 0)) {
            ErrorMessage.fire({
                title: 'Check how long the code lasts',
                text: 'Give a number of hours greater than zero, or leave it blank so the code never expires.'
            });
            return;
        }
        // A backwards window is never what anyone meant, and it would hide the
        // offer forever without saying so.
        if (startsAt && endsAt && endsAt <= startsAt) {
            ErrorMessage.fire({
                title: 'Check the dates',
                text: 'The offer has to end after it starts. Leave either one blank if you do not want that boundary.'
            });
            return;
        }
        if (type === 'coded' && expiresAtDateTime && expiresAtDateTime <= Date.now()) {
            const proceed = await Swal.fire({
                title: 'That use-by date has passed',
                text: 'Codes for this offer are already expired, so no fan will be able to use one. Save it anyway?',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Save anyway'
            });
            if (!proceed.value) return;
        }
        this.setState({ loading: true });
        try {
            const id = editingId || appDatabasePrimaryFunctions.ref('offers').push().key;
            const existing = offers[id] || {};
            const payload = {
                title: title.trim(),
                description: this.state.description || '',
                imageUrl: this.state.imageUrl || '',
                type: type || 'link',
                // experienceId is what an OLD mobile build reads, so it stays
                // populated; experienceIds is the full set for new builds
                ...experienceFieldsFor(
                    this.state.selectedExperienceIds,
                    this.experienceOptions(),
                    this.state.scope
                ),
                audience: audience || 'all',
                team: audience === 'stm' ? (this.state.team || '') : null,
                startsAt: startsAt,
                endsAt: endsAt,
                // Only a coded offer has a code to expire; a link offer keeps
                // working as long as the offer itself is on the shelf.
                expiresAtHours: type === 'coded' ? expiresAtHours : null,
                expiresAtDateTime: type === 'coded' ? expiresAtDateTime : null,
                codeSource: type === 'coded' ? (this.state.codeSource || 'pool') : null,
                // An issued offer only gets here with its format unchanged, and
                // update() leaves unwritten fields alone — so its saved format is
                // kept byte for byte rather than rewritten from the form.
                ...(!derived ? { codePrefix: null, codeLength: null } : (issued ? {} : this.codeFormatOf(this.state))),
                buttonText: type === 'link' ? (this.state.buttonText || '') : null,
                clickUrl: type === 'link' ? (this.state.clickUrl || '') : null,
                order: order ? parseInt(order, 10) : (existing.order || Object.keys(offers).length + 1),
                active: this.state.active !== false
            };
            if (!editingId) {
                payload.createdAt = Date.now();
            }
            // update() (not set) so the server-maintained codePoolSize /
            // codesAssigned counters survive edits
            await appDatabasePrimaryFunctions.ref(`offers/${id}`).update(payload);

            // A coded offer has nowhere to put codes until it exists, so
            // saving it is step one of two — stay in the modal and move to the
            // pool rather than making someone find the offer again and reopen
            // it. Only when the pool is already stocked is saving the end.
            const poolAlreadyStocked = (offers[id] || {}).codePoolSize > 0;
            // Nothing to upload for a built code — the offer is finished the
            // moment it is saved.
            const needsCodes = type === 'coded' && this.state.codeSource !== 'derived';
            if (needsCodes && !poolAlreadyStocked) {
                Toast.fire({ title: editingId ? 'Offer saved — now add codes' : 'Offer created — now add codes' });
                await this.loadData();
                this.setState({ editingId: id, step: 'codes', codesText: '', loading: false });
                return;
            }

            Toast.fire({ title: editingId ? 'Offer updated!' : 'Offer created!' });
            this.clearForm();
            await this.loadData();
        } catch (e) {
            console.log('Error saving offer:', e);
            // The database rules refuse a change to how a built offer makes
            // codes once any fan holds one — and they check the assignments
            // themselves, which this screen cannot read, so they can refuse
            // when the issued count here still says zero. Say that rather than
            // sending someone to check their connection.
            const refused = /permission_denied/i.test(String((e && (e.code || e.message)) || ''));
            ErrorMessage.fire(refused && saved && this.isDerived(saved)
                ? {
                    title: 'Codes already issued',
                    // Hedged: losing admin access mid-session is refused the same way.
                    text: 'The database refused this change. Once a fan holds a code built from this offer, its type, team,'
                        + ' code source, prefix and length can no longer change — to change them, make a new offer. If you'
                        + ' changed none of those, check you are still signed in as an admin.'
                }
                : { title: 'Uh oh!', text: 'Something went wrong saving the offer, check your connection and try again.' });
            this.setState({ loading: false });
        }
    }

    async deleteOffer(id) {
        const offer = this.state.offers[id] || {};
        const result = await Swal.fire({
            title: `Delete "${offer.title || 'this offer'}"?`,
            text: 'Fans lose access immediately. Any uploaded codes and existing assignments are retained server-side.',
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete'
        });
        if (!result.value) return;
        this.setState({ loading: true });
        try {
            await appDatabasePrimaryFunctions.ref(`offers/${id}`).remove();
            Toast.fire({ title: 'Offer deleted' });
            if (this.state.editingId === id) this.clearForm();
            await this.loadData();
        } catch (e) {
            console.log('Error deleting offer:', e);
            ErrorMessage.fire({ title: 'Uh oh!', text: 'Something went wrong deleting the offer.' });
            this.setState({ loading: false });
        }
    }

    async uploadCodes() {
        const { editingId, codesText } = this.state;
        const codes = (codesText || '').split('\n').map(line => line.trim()).filter(line => line.length > 0);
        if (codes.length === 0) {
            ErrorMessage.fire({ title: 'No codes found', text: 'Paste at least one code — one per line.' });
            return;
        }
        // Fetch a fresh ID token at upload time — the one cached at mount
        // expires after ~an hour and would 401 a long editing session
        let token = null;
        try {
            token = this.props.userData ? await this.props.userData.getIdToken() : null;
        } catch (e) {
            console.log('Error refreshing auth token:', e);
        }
        if (!token) {
            ErrorMessage.fire({ title: 'Not authenticated', text: 'Could not get an auth token, refresh the page and try again.' });
            return;
        }
        this.setState({ loading: true });
        try {
            const response = await fetch(apiUrl + "uploadOfferCodes", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ offerId: editingId, codes })
            });
            const { ok, data, error } = await readApiResponse(response);
            if (ok) {
                Toast.fire({ title: `Added ${data.added} codes — ${data.poolSize} now in the pool` });
                this.setState({ codesText: '' });
                await this.loadData();
            } else {
                ErrorMessage.fire({ title: 'Upload failed', text: error });
                this.setState({ loading: false });
            }
        } catch (e) {
            console.log('Error uploading codes:', e);
            ErrorMessage.fire({ title: 'Upload failed', text: uploadFailureText(usingLocalApi) });
            this.setState({ loading: false });
        }
    }

    // Plain-English statement of what saving will actually do. The form
    // changes shape across four states (all/STM x link/coded) and nothing
    // used to explain why fields appeared or what the combination meant.
    // A coded offer cannot hold codes until it exists, so for one the primary
    // action is not "create" — it is "get me to the codes".
    // "Hidden" and "Scheduled" are different problems: one is somebody flipped
    // the switch, the other is the clock. A single Live/Hidden pill made an
    // offer that had simply not started yet look like a mistake.
    statusLabel(offer) {
        if (offer.active === false) return 'Hidden';
        const state = offerWindowState(offer);
        if (state === OFFER_SCHEDULED) return 'Scheduled';
        if (state === OFFER_ENDED) return 'Ended';
        return 'Live';
    }

    primaryActionLabel() {
        const { type, editingId, offers } = this.state;
        const stocked = editingId && (offers[editingId] || {}).codePoolSize > 0;
        if (type === 'coded' && this.state.codeSource === 'derived') {
            return editingId ? 'Save changes' : 'Create offer';
        }
        if (type === 'coded' && !stocked) return 'Add codes';
        return editingId ? 'Save changes' : 'Create offer';
    }

    // Leaving the codes step with an empty pool is allowed — you may not have
    // the codes yet — but not silently, because the offer already exists and,
    // if it is live, already errors for fans.
    async finishLater() {
        const { editingId, offers, active } = this.state;
        const pool = (offers[editingId] || {}).codePoolSize || 0;
        if (pool === 0 && active) {
            const result = await Swal.fire({
                title: 'Leave it live with no codes?',
                text: 'The offer exists and is live, but its pool is empty — any fan who opens it gets an error. You can turn Visibility off instead and come back when you have codes.',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Leave it live',
                cancelButtonText: 'Keep editing'
            });
            if (!result.value) return;
        }
        this.clearForm();
    }

    finishCodes() {
        this.clearForm();
    }

    consequenceText() {
        const { audience, type, active, team, scope, editingId, offers } = this.state;
        const audienceName = audience === 'stm'
            ? `Only ${this.experienceName(team)} season ticket members who have verified with Ticketmaster`
            : (this.hasExperiences()
                ? `Every ${this.experienceName(scope)} app member`
                : 'Every app member');

        // Codes can only be attached to a SAVED offer, so a coded offer always
        // starts life with an empty pool. Saying "assigned one code from the
        // pool" here describes a pool that does not exist yet — and this box is
        // the most prominent thing on the modal, so it has to say what actually
        // happens next rather than what happens eventually.
        const savedOffer = editingId ? (offers || {})[editingId] : null;
        const hasCodes = !!(savedOffer && savedOffer.codePoolSize > 0);

        let how;
        if (type === 'coded' && this.state.codeSource === 'derived') {
            // Nothing is uploaded and nothing runs out, so the pool warning
            // below would describe a step that never happens.
            const prefix = (this.state.codePrefix || '').trim().toUpperCase();
            const fixedLength = hasFixedLength(this.state.codeLength)
                && !codeLengthProblem(prefix, this.state.codeLength);
            how = fixedLength
                ? ` Each fan is shown a ${Number(String(this.state.codeLength).trim())}-character code: ${prefix || 'the prefix'},`
                    + ' zeros to fill, then their own Ticketmaster account number. They keep it.'
                : ` Each fan is shown ${prefix || 'the prefix'} followed by their own Ticketmaster account number, and keeps it.`;
        } else if (type === 'coded' && !hasCodes) {
            how = ' Saving creates it and takes you straight to its code pool — it holds no codes until you add them there.'
                + (active
                    ? ' Until you do, any fan who opens it gets an error.'
                    : '');
        } else if (type === 'coded') {
            how = ' Each fan who opens it is assigned one code from the pool and keeps it.';
        } else {
            how = ' Tapping it sends fans to your link.';
        }

        const window = describeWindow({
            startsAt: dateTimeInputToEpoch(this.state.startsAt),
            endsAt: dateTimeInputToEpoch(this.state.endsAt)
        });
        const when = window ? ` It runs ${window}.` : '';
        const life = type === 'coded'
            ? describeCodeExpiry({
                expiresAtHours: (this.state.expiresAtHours || '').trim() || null,
                expiresAtDateTime: dateTimeInputToEpoch(this.state.expiresAtDateTime)
            })
            : '';
        const codeUseBy = dateTimeInputToEpoch(this.state.expiresAtDateTime);
        // v1 hid this by silently substituting a month, which turned a dead
        // promotion back on. Saying it is the whole point of not doing that.
        const alreadyDead = type === 'coded' && codeUseBy && codeUseBy <= Date.now()
            ? ' Its codes have ALREADY expired, so no fan can use one.'
            : '';
        const lasts = life ? ` Each code works ${life}.` : '';
        const hidden = active ? '' : ' It stays hidden from fans until you turn Visibility on.';
        return `${audienceName} sees this offer.${how}${when}${lasts}${alreadyDead}${hidden}`;
    }

    render() {
        const { offers, experiences, editingId, scope, step } = this.state;
        // Only this experience's offers. An offer belongs to one experience,
        // and showing another one's here would invite editing the wrong app.
        const experienceOptions = this.experienceOptions();
        // Named, not counted: "Experience 1 and Stadium Experience" tells an
        // operator whether the offer is where they meant it to be; "2
        // experiences" makes them open the list to find out.
        const selectedExperienceNames = this.state.selectedExperienceIds
            .map(id => (experienceOptions.find(exp => exp.id === id) || {}).name)
            .filter(Boolean)
            .join(', ');
        const activeExperience = scope
            ? experienceOptions.find(exp => exp.id === scope)
            : experienceOptions.find(exp => exp.isDefault);
        // Same matcher the fan app uses, so what an operator sees here is what
        // fans get — including offers shared across several experiences.
        const offerList = Object.entries(offers)
            .map(([id, offer]) => ({ id, ...offer }))
            .filter(offer => offerMatchesExperience(offer, activeExperience || null))
            .sort((a, b) => (a.order || 0) - (b.order || 0));
        const experienceList = Object.entries(experiences)
            .map(([id, exp]) => ({ id, ...exp }))
            .sort((a, b) => (a.order || 0) - (b.order || 0));
        // A tenant with no experiences has no name for where its offers run,
        // and "in Default" is a concept it should never have to meet.
        const inScope = this.hasExperiences() ? ` in ${this.experienceName(scope)}` : '';
        // A members-only offer keys entitlements/{uid}/{teamId}. That id has
        // never had to be an experience — the server takes any string — so a
        // club with no experiences gets one option standing for itself rather
        // than being told to invent a second app skin first.
        const teamExperienceList = teamIdOptions(experienceList);
        // Code upload only applies once a coded offer has been SAVED —
        // the saved record is what the server assigns codes against
        const savedOffer = editingId ? offers[editingId] : null;
        // A derived offer has no pool, so no pool button and no pool step.
        const showCodeUpload = !!(savedOffer && savedOffer.type === 'coded' && !this.isDerived(savedOffer));

        const liveCount = offerList.filter(o => o.active !== false).length;
        const poolState = showCodeUpload ? this.poolState(savedOffer) : 'ok';
        const poolSize = showCodeUpload ? (savedOffer.codePoolSize || 0) : 0;
        const poolAssigned = showCodeUpload ? (savedOffer.codesAssigned || 0) : 0;
        const poolRemaining = showCodeUpload ? this.poolRemaining(savedOffer) : 0;
        const poolPct = poolSize > 0 ? (poolRemaining / poolSize) * 100 : 0;
        const poolChip = poolState === 'out' ? 'Empty' : (poolState === 'low' ? 'Running out' : 'Healthy');
        const poolMsg = poolState === 'out'
            ? 'Fans who open this offer get an error until you add codes.'
            : (poolState === 'low'
                ? `${poolAssigned} assigned. Fans hit an error when the last code goes.`
                : `${poolAssigned} of ${poolSize} assigned.`);

        return (
            <div className="admin-wrapper">
                <div className="loading-screen" style={{ display: this.state.loading ? 'block' : 'none' }}/>
                <SideMenu/>
                <TopMenu/>
                <div className="admin-main-panel">

                    <div className="admin-page-head">
                        <p className="admin-eyebrow">Fan engagement</p>
                        <p className="admin-header-text">Offers</p>
                        <p className="admin-page-sub">
                            Sponsor and team deals fans see in the app. Link offers send fans to a URL.
                            Coded offers hand each fan one code, from a list you upload
                            {identityIsVerifiable(this.state.serverCapabilities) && ' or built from their Ticketmaster account'}.
                        </p>
                    </div>

                    <div className="admin-toolbar">
                        <button className="btn btn-primary" onClick={this.toggleModal}>Add offer</button>
                        <span className="admin-toolbar-end">
                            <span className="admin-list-head-count">
                                {offerList.length} {offerList.length === 1 ? 'offer' : 'offers'}{inScope}
                                {offerList.length > 0 && ` \u00b7 ${liveCount} live`}
                            </span>
                        </span>
                    </div>

                    {offerList.length === 0 &&
                        <div className="admin-empty" style={{ margin: '18px 24px 0' }}>
                            <p className="admin-empty-title">No offers{inScope} yet</p>
                            <p className="admin-empty-body">
                                Offers are sponsor and team deals fans see
                                {this.hasExperiences() ? ' in this experience' : ' in the app'}. Add one to get started.
                            </p>
                        </div>
                    }

                    {offerList.length > 0 &&
                        <>
                            <div className="admin-table-wrap has-cardlist">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Offer</th>
                                            <th>Audience</th>
                                            <th>Redeem</th>
                                            <th>Code pool</th>
                                            <th>Status</th>
                                            <th/>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {offerList.map(offer => (
                                            <tr key={offer.id} data-state={offer.type === 'coded' ? this.poolState(offer) : undefined}>
                                                <td>{offer.order || '-'}</td>
                                                <td className="admin-cell-title">
                                                    {offer.title}
                                                    {offer.description && <small>{offer.description}</small>}
                                                </td>
                                                <td>
                                                    {offer.audience === 'stm'
                                                        ? <span className="admin-tag is-strong">STM &middot; {this.experienceName(offer.team)}</span>
                                                        : <span className="admin-tag">Everyone</span>}
                                                    {isSharedAcrossExperiences(offer) &&
                                                        <span className="admin-tag" title="Editing this offer changes it in every experience it appears in" style={{ marginLeft: 5 }}>
                                                            Shared
                                                        </span>
                                                    }
                                                </td>
                                                <td>
                                                    <span className="admin-tag">{offer.type === 'coded' ? 'Coded' : 'Link'}</span>
                                                    {/* The second clock. Without it the list can say
                                                        Live while every code it hands out is dead. */}
                                                    {this.codeExpiryNote(offer)}
                                                </td>
                                                {/* Flag an empty or nearly-drained pool: fans hit a hard
                                                    error the moment the last code goes */}
                                                <td>{this.renderMiniRoll(offer)}</td>
                                                <td>
                                                    <span
                                                        className={`admin-live${this.statusLabel(offer) === 'Live' ? '' : ' is-off'}`}
                                                        title={describeWindow(offer) || undefined}
                                                    >
                                                        {this.statusLabel(offer)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="admin-row-actions">
                                                        <button type="button" className="admin-icon-btn" aria-label="Edit offer" title="Edit offer" onClick={() => this.startEdit(offer.id)}><span className="fa fa-pencil" aria-hidden="true"/></button>
                                                        <button type="button" className="admin-icon-btn is-danger" aria-label="Delete offer" title="Delete offer" onClick={() => this.deleteOffer(offer.id)}><span className="fa fa-trash-o" aria-hidden="true"/></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* same data, stacked — an 8-column table cannot work on a phone */}
                            <div className="admin-cardlist">
                                {offerList.map(offer => (
                                    <div className="admin-ocard" key={offer.id} data-state={offer.type === 'coded' ? this.poolState(offer) : undefined}>
                                        <div className="admin-ocard-top">
                                            <span className="ord">{offer.order || '-'}</span>
                                            <h4>{offer.title}</h4>
                                        </div>
                                        <div className="meta">
                                            {offer.audience === 'stm'
                                                ? <span className="admin-tag is-strong">STM &middot; {this.experienceName(offer.team)}</span>
                                                : <span className="admin-tag">Everyone</span>}
                                            <span className="admin-tag">{offer.type === 'coded' ? 'Coded' : 'Link'}</span>
                                            {isSharedAcrossExperiences(offer) && <span className="admin-tag">Shared</span>}
                                        </div>
                                        {offer.type === 'coded' && this.renderMiniRoll(offer)}
                                        <div className="admin-ocard-foot">
                                            <span
                                                className={`admin-live${this.statusLabel(offer) === 'Live' ? '' : ' is-off'}`}
                                                title={describeWindow(offer) || undefined}
                                            >
                                                {this.statusLabel(offer)}
                                            </span>
                                            <div className="admin-row-actions">
                                                <button type="button" className="admin-icon-btn" aria-label="Edit offer" title="Edit offer" onClick={() => this.startEdit(offer.id)}><span className="fa fa-pencil" aria-hidden="true"/></button>
                                                <button type="button" className="admin-icon-btn is-danger" aria-label="Delete offer" title="Delete offer" onClick={() => this.deleteOffer(offer.id)}><span className="fa fa-trash-o" aria-hidden="true"/></button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    }
                </div>

                {/* The form lives in a modal so the list is what you land on —
                    and so this screen matches Home Buttons, Side Menu and the
                    rest. The consequence line moved to the bottom of the form
                    rather than a side rail: you read what will happen right
                    before you commit, not off to one side. */}
                <Modal isOpen={this.state.modal} toggle={this.toggleModal} style={{ width: '90%' }} id="offerModal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {editingId
                                    ? `Editing: ${offers[editingId] ? offers[editingId].title : editingId}`
                                    : (this.hasExperiences() ? `Add an offer to ${this.experienceName(scope)}` : 'Add an offer')}
                            </h5>
                            <button type="button" className="admin-modal-close" aria-label="Close" onClick={this.toggleModal}>
                                &times;
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="question-box">
                                {(this.state.type === 'coded' || step === 'codes') &&
                                    <div className="admin-steps">
                                        <span className={`admin-step${step === 'details' ? ' is-current' : ' is-done'}`}>
                                            1. Details
                                        </span>
                                        <span className="admin-step-sep" aria-hidden="true">&rarr;</span>
                                        {showCodeUpload
                                            ? <button
                                                type="button"
                                                className={`admin-step as-link${step === 'codes' ? ' is-current' : ''}`}
                                                onClick={() => this.setState({ step: 'codes' })}
                                              >
                                                2. Codes
                                              </button>
                                            : <span className={`admin-step${step === 'codes' ? ' is-current' : ''}`}>
                                                2. Codes
                                              </span>
                                        }
                                    </div>
                                }
    {step === 'details' && <>
                                <fieldset className="admin-fieldset">
                                    <legend className="admin-legend">What fans see</legend>

                                    <div className="form-group">
                                        <label htmlFor="title">Title</label>
                                        <p className="admin-hint">Shown on the offer card. 50 characters max.</p>
                                        <input type="text" id="title" name="title" className="form-control" maxLength={50} value={this.state.title} onChange={this.handleChange} placeholder="2-for-1 Tickets"/>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="description">Description</label>
                                        <p className="admin-hint">Details fans see when they open the offer.</p>
                                        <textarea id="description" name="description" className="form-control" rows={3} value={this.state.description} onChange={this.handleChange} placeholder="Show your code at the team store to redeem"/>
                                    </div>

                                    {this.renderImageUpload('Offer image', 'imageUrl')}
                                </fieldset>

                                <fieldset className="admin-fieldset">
                                    <legend className="admin-legend">Who sees it</legend>

                                    {/* An offer can run across several experiences —
                                        a sponsor deal that belongs to the whole app
                                        rather than one version of it. */}
                                    {/* One experience is the answer nearly every time, and the
                                        modal title already says which. Showing every experience as
                                        an open choice made this read as a second scope picker
                                        competing with the one in the top bar. So the normal case
                                        states where the offer runs, and the multi-experience path
                                        — a sponsor across two teams sharing an arena — is one
                                        click away rather than in the way. */}
                                    {experienceOptions.length > 1 &&
                                        <div className="form-group">
                                            <label>Where this offer runs</label>
                                            <p className="admin-hint">
                                                {selectedExperienceNames || 'Nowhere yet — pick at least one below.'}
                                            </p>
                                            {!this.state.showOtherExperiences &&
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={() => this.setState({ showOtherExperiences: true })}
                                                >
                                                    Also show this in other experiences
                                                </button>
                                            }
                                            {this.state.showOtherExperiences &&
                                            <div className="admin-checkbox-group">
                                                {experienceOptions.map(exp => {
                                                    const checked = this.state.selectedExperienceIds.indexOf(exp.id) !== -1;
                                                    return (
                                                        <div className="form-check" key={exp.id}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                id={`offerExp-${exp.id}`}
                                                                checked={checked}
                                                                onChange={() => this.toggleExperience(exp.id)}
                                                            />
                                                            <label className="form-check-label" htmlFor={`offerExp-${exp.id}`}>
                                                                {exp.name}
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            }
                                            {this.state.selectedExperienceIds.length > 1 &&
                                                <p className="admin-note">
                                                    This offer appears in {this.state.selectedExperienceIds.length} experiences.
                                                    Editing it here changes it in all of them
                                                    {this.state.type === 'coded' && ', and they draw from the same pool of codes'}.
                                                </p>
                                            }
                                        </div>
                                    }

                                    <div className="admin-row">
                                        <div className="form-group">
                                            <label htmlFor="audience">Audience</label>
                                            <p className="admin-hint">Who the offer is visible to.</p>
                                            <select id="audience" name="audience" className="form-control" value={this.state.audience} onChange={this.handleChange}>
                                                <option value="all">All app members</option>
                                                {/* Season ticket members are the members of a TEAM experience —
                                                    entitlements are keyed on one. With no team experience there is
                                                    nothing to pick in the Team box below, so choosing this could
                                                    only ever dead-end at "Missing team" on save. An offer already
                                                    saved as STM keeps the option so editing it does not silently
                                                    change its audience. */}
                                                {(teamExperienceList.length > 0 || this.state.audience === 'stm') &&
                                                    <option value="stm">Season Ticket Members only</option>
                                                }
                                            </select>
                                            {/* Say what this does and does not do, at the moment
                                                the choice is made. "Members only" reads as access
                                                control, and on a LINK offer it is not one: the
                                                node offers live in is world-readable, and the
                                                enforcement lives in the claim endpoint, which a
                                                link offer never calls. The link IS the reward,
                                                so there is nothing else standing in front of it. */}
                                            {this.state.audience === 'stm' && this.state.type === 'link' &&
                                                <p className="admin-hint">
                                                    This hides the offer from everyone else in the app, but it does
                                                    not make the link itself secret &mdash; anyone who is given it can
                                                    open it. Fine for something you would be relaxed about being
                                                    forwarded. If it has to be limited to the people on your list,
                                                    use a code offer instead: those are checked on our side before a
                                                    fan is given one.
                                                </p>
                                            }
                                        </div>
                                    </div>

                                    {this.state.audience === 'stm' &&
                                        <div className="admin-reveal">
                                            {teamExperienceList.length > 0 &&
                                                <div className="form-group">
                                                    <label htmlFor="team">Team</label>
                                                    <p className="admin-hint">Whose season ticket members this offer is for.</p>
                                                    {/* team must always be a REAL experience id — it keys entitlements/{uid}/{team} */}
                                                    <select id="team" name="team" className="form-control" style={{ maxWidth: 320 }} value={this.state.team} onChange={this.handleChange} disabled={this.derivedCodesIssued(this.state.editingId) > 0}>
                                                        <option value="">Select a team&hellip;</option>
                                                        {teamExperienceList.map(exp => (
                                                            <option key={exp.id} value={exp.id}>{exp.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            }
                                            <p className="admin-note">
                                                Season Ticket Member offers stay hidden until a fan taps <b>Verify Status</b> on their
                                                profile and their Ticketmaster login matches this team&rsquo;s season-ticket roster.
                                            </p>
                                        </div>
                                    }
                                </fieldset>

                                <fieldset className="admin-fieldset">
                                    <legend className="admin-legend">How they redeem it</legend>

                                    <div className="form-group" style={{ maxWidth: 320 }}>
                                        <label htmlFor="type">Type</label>
                                        <select id="type" name="type" className="form-control" value={this.state.type} onChange={this.handleChange} disabled={this.derivedCodesIssued(this.state.editingId) > 0}>
                                            <option value="link">Link offer &mdash; send fans to a URL</option>
                                            <option value="coded">Coded offer &mdash; give each fan a code</option>
                                        </select>
                                    </div>

                                    {this.state.type === 'link' &&
                                        <div className="admin-reveal">
                                            <div className="form-group" style={{ maxWidth: 320 }}>
                                                <label htmlFor="buttonText">Button text</label>
                                                <input type="text" id="buttonText" name="buttonText" className="form-control" value={this.state.buttonText} onChange={this.handleChange} placeholder="Redeem Now"/>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="clickUrl">Click-through URL</label>
                                                <input type="url" id="clickUrl" name="clickUrl" className="form-control" value={this.state.clickUrl} onChange={this.handleChange} placeholder="https://example.com/offer"/>
                                            </div>
                                        </div>
                                    }

                                    {this.state.type === 'coded' &&
                                        <div className="admin-reveal">
                                            <div className="form-group" style={{ maxWidth: 420 }}>
                                                <label htmlFor="codeSource">Where the codes come from</label>
                                                <select id="codeSource" name="codeSource" className="form-control" value={this.state.codeSource} onChange={this.handleChange} disabled={this.derivedCodesIssued(this.state.editingId) > 0}>
                                                    <option value="pool">A list you upload &mdash; each fan gets the next one</option>
                                                    {/* A built code is the fan's Ticketmaster account number with a
                                                        prefix, so a club without Ticketmaster has nothing to build one
                                                        from. Kept on screen for an offer that already uses it, the way
                                                        Feature Access keeps a live switch it cannot otherwise offer —
                                                        hiding it would silently save the offer back as a pool. */}
                                                    {(identityIsVerifiable(this.state.serverCapabilities) || this.state.codeSource === 'derived') &&
                                                        <option value="derived" disabled={this.listCodesIssued(this.state.editingId) > 0}>Built from the fan&rsquo;s Ticketmaster account</option>
                                                    }
                                                </select>
                                                {this.listCodesIssued(this.state.editingId) > 0 && this.state.codeSource !== 'derived' &&
                                                    <p className="admin-hint" style={{ marginTop: 6 }}>
                                                        Fans already hold codes from the uploaded list, so this offer can&rsquo;t switch to built codes. Make a new offer for that.
                                                    </p>
                                                }
                                            </div>

                                            {this.state.codeSource === 'derived' && this.renderDerivedCodeFormat()}
                                        </div>
                                    }

                                    {this.state.type === 'coded' &&
                                        <div className="admin-reveal admin-row">
                                            {/* A second clock, and not the one above: Starts and
                                                Ends decide when the offer is on the shelf, these
                                                decide how long what a fan took off it keeps
                                                working. Blank means never — unlike v1's MLB
                                                rewards, where blank quietly meant one month. */}
                                            <div className="form-group" style={{ maxWidth: 200 }}>
                                                <label htmlFor="expiresAtHours">Code good for</label>
                                                <p className="admin-hint">Hours after a fan gets it. Blank never expires.</p>
                                                <input type="number" min="1" step="1" id="expiresAtHours" name="expiresAtHours" className="form-control" value={this.state.expiresAtHours} onChange={this.handleChange} placeholder="24"/>
                                            </div>
                                            <div className="form-group" style={{ maxWidth: 230 }}>
                                                <label htmlFor="expiresAtDateTime">Code use by</label>
                                                <p className="admin-hint">However long ago they got it. Blank never expires.</p>
                                                <input type="datetime-local" id="expiresAtDateTime" name="expiresAtDateTime" className="form-control" value={this.state.expiresAtDateTime} onChange={this.handleChange}/>
                                            </div>
                                        </div>
                                    }

                                    {this.state.type === 'coded' && this.state.codeSource === 'derived' &&
                                        <p className="admin-note admin-reveal">
                                            Nothing to upload. Every verified season ticket member gets their own code, it never
                                            changes, and it cannot run out. The card numbers have to exist at the point of sale
                                            already &mdash; that is the list you sent them.
                                        </p>
                                    }

                                    {this.state.type === 'coded' && this.state.codeSource !== 'derived' && !showCodeUpload &&
                                        <p className="admin-note admin-reveal">
                                            Codes are added in the next step &mdash; saving creates the offer and takes you straight to its <b>Code pool</b>.
                                        </p>
                                    }

                                    {this.state.type === 'coded' && this.state.codeSource !== 'derived' && showCodeUpload &&
                                        <p className="admin-note admin-reveal">
                                            Each fan who opens this offer is assigned one code and keeps it.
                                            Add or check codes in the <b>Code pool</b>, from the button below.
                                        </p>
                                    }
                                </fieldset>

                                <fieldset className="admin-fieldset">
                                    <legend className="admin-legend">When and where it sits</legend>
                                    <div className="admin-row">
                                        {/* maxWidth, not flex-basis: once .admin-row stacks on a
                                            phone, a flex basis becomes a height and leaves a gap */}
                                        <div className="form-group" style={{ maxWidth: 130 }}>
                                            <label htmlFor="order">Order</label>
                                            <p className="admin-hint">1 shows first.</p>
                                            <input type="number" id="order" name="order" className="form-control" value={this.state.order} onChange={this.handleChange} placeholder="1"/>
                                        </div>
                                        {/* Blank means unbounded, which is how every offer
                                            behaved before this existed — so an operator who
                                            ignores these two fields gets the old behaviour. */}
                                        <div className="form-group" style={{ maxWidth: 230 }}>
                                            <label htmlFor="startsAt">Starts</label>
                                            <p className="admin-hint">Blank starts it now.</p>
                                            <input type="datetime-local" id="startsAt" name="startsAt" className="form-control" value={this.state.startsAt} onChange={this.handleChange}/>
                                        </div>
                                        <div className="form-group" style={{ maxWidth: 230 }}>
                                            <label htmlFor="endsAt">Ends</label>
                                            <p className="admin-hint">Blank runs until you hide it.</p>
                                            <input type="datetime-local" id="endsAt" name="endsAt" className="form-control" value={this.state.endsAt} onChange={this.handleChange}/>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="active">Visibility</label>
                                            <p className="admin-hint">Inactive offers are hidden from fans.</p>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <label className="switch" style={{ width: 60, marginBottom: 0 }}>
                                                    <input name="active" id="active" type="checkbox" checked={this.state.active} onChange={this.handleChange}/>
                                                    <span className="slider round"/>
                                                </label>
                                                <span style={{ fontSize: 13, fontWeight: 600, color: this.state.active ? 'var(--ink)' : 'var(--slate)' }}>
                                                    {this.state.active ? 'Live in the app' : 'Hidden from fans'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                                </>}

                                {step === 'codes' && showCodeUpload &&
                                    <div className="admin-roll-card" data-state={poolState} style={{ marginTop: 18 }}>
                                        <p className="admin-eyebrow">Code pool</p>
                                        <div className="admin-roll-count">
                                            <span className="n">{poolRemaining}</span>
                                            <span className="of">left of {poolSize}</span>
                                        </div>
                                        <div className="admin-roll">
                                            <div className="admin-roll-fill" style={{ width: `${poolPct}%` }}/>
                                            <div className="admin-roll-perf"/>
                                            <div className="admin-roll-edge"/>
                                        </div>
                                        <div className="admin-roll-foot">
                                            <span className="admin-roll-chip">{poolChip}</span>
                                            <span>{poolMsg}</span>
                                        </div>
                                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line-soft)' }}>
                                            <div className="form-group">
                                                <label htmlFor="codesText">Add codes</label>
                                                <p className="admin-hint">One per line. They join the pool immediately.</p>
                                                <textarea id="codesText" name="codesText" className="form-control" rows={5} style={{ fontFamily: 'var(--admin-mono)', fontSize: 12 }} value={this.state.codesText} onChange={this.handleChange} placeholder={"CODE-001\nCODE-002\nCODE-003"}/>
                                            </div>
                                            <button className="btn btn-primary btn-sm" onClick={this.uploadCodes}>Add to pool</button>
                                        </div>
                                    </div>
                                }

                                {step === 'details' &&
                                <div className="admin-consequence" style={{ marginTop: 18 }}>
                                    <p className="admin-eyebrow">When you save</p>
                                    <p>{this.consequenceText()}</p>
                                    <span className="admin-chip-who">
                                        {this.state.audience === 'stm' ? 'SEASON TICKET MEMBERS' : 'ALL APP MEMBERS'}
                                    </span>
                                </div>}

                                {step === 'details' &&
                                    <div className="admin-modal-actions">
                                        <button type="button" className="btn btn-secondary" onClick={this.toggleModal}>Cancel</button>
                                        {showCodeUpload &&
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={() => this.setState({ step: 'codes' })}
                                            >
                                                Code pool &middot; {poolRemaining} left
                                            </button>
                                        }
                                        <button type="button" className="btn btn-primary" onClick={this.saveOffer}>
                                            {this.primaryActionLabel()}
                                        </button>
                                    </div>
                                }

                                {step === 'codes' &&
                                    <div className="admin-modal-actions">
                                        <button type="button" className="btn btn-secondary" onClick={() => this.setState({ step: 'details' })}>
                                            Back to details
                                        </button>
                                        <button type="button" className="btn btn-secondary" onClick={this.finishLater}>
                                            Finish later
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            disabled={poolSize === 0}
                                            title={poolSize === 0 ? 'Add at least one code first' : undefined}
                                            onClick={this.finishCodes}
                                        >
                                            Done
                                        </button>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </Modal>
            </div>
        );
    }
}

export default SetUpOffers;
